/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { AddressFormActions } from '@/components/blocks/AddressForm/parts/Actions';
import { AddressFormFields } from '@/components/blocks/AddressForm/parts/Fields';
import { addressFormSX } from '@/components/blocks/AddressForm/styles/form';
import { useForm } from '@/utils/useForm';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { useCountry } from '@/data/Content/Country';
import { useLocalization } from '@/data/Localization';
import { EditableAddress, AddressFormActionLabels } from '@/data/types/Address';
import { Stack, Divider } from '@mui/material';
import { useMemo, FC } from 'react';

type Props = {
	addressInput: EditableAddress;
	showAddressType?: boolean;
	onSubmit: (address: EditableAddress) => void;
	onCancel?: () => void;
	submitLabel: AddressFormActionLabels;
	cancelLabel?: AddressFormActionLabels;
	formLabel?: JSX.Element;
	editPerson?: boolean;
	hasPrimaryAdd?: boolean;
	addressBook?: boolean;
};

export const splitAddressLine1 = (input: EditableAddress): EditableAddress => {
	const raw = input.addressLine1 ?? '';
	const parts = raw.split('|');
	if (parts.length < 2) return input;
	return {
		...input,
		addressLine1: parts[0],
		xaddr_numExt: parts[1] ?? input.xaddr_numExt,
		xaddr_numInt: parts[2] ?? input.xaddr_numInt,
	};
};

export const AddressForm: FC<Props> = ({
	addressInput,
	showAddressType = false,
	onSubmit,
	onCancel,
	submitLabel,
	cancelLabel,
	formLabel,
	editPerson,
	hasPrimaryAdd,
	addressBook,
}) => {
	const nickNameDisabled = useMemo(() => Boolean(addressInput.nickName), [addressInput]);

	const initialAddress = useMemo(
		() => (nickNameDisabled ? splitAddressLine1(addressInput) : addressInput),
		[addressInput]
	);

	const {
		values,
		handleAutoCompleteInputChange,
		handleInputChange,
		handleSelectChange,
		handleSubmit,
		error,
		formRef,
		submitting,
		setSubmitting,
		setError,
	} = useForm<EditableAddress>(initialAddress);

	const address: EditableAddress = values;

	const addressFormNLS = useLocalization('AddressForm');
	const { countries } = useCountry();
	const helperText = useMemo(
		() => ({
			nickName: error.nickName ? addressFormNLS.Msgs.InvalidAddressName.t() : EMPTY_STRING,
			phone1: error.phone1 ? addressFormNLS.Msgs.InvalidFormat.t() : EMPTY_STRING,
			email1: error.email1 ? addressFormNLS.Msgs.InvalidFormat.t() : EMPTY_STRING,
		}),
		[addressFormNLS, error]
	);

	const handleWrapSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (address.addressLine2 && !isNaN(Number(address.addressLine2.replace(/\s/g, '')))) {
			address.xaddr_numInt = address.addressLine2;
			setError?.({ ...error, addressLine2: true });
			return;
		}

		handleSubmit(onSubmit)(e);
	};

	return (
		<Stack
			spacing={2}
			divider={<Divider />}
			component="form"
			noValidate
			onSubmit={handleWrapSubmit}
			ref={formRef}
			sx={addressFormSX}
		>
			<AddressFormFields
				{...{
					address,
					handleAutoCompleteInputChange,
					handleChange: handleInputChange,
					selectChange: handleSelectChange,
					nickNameDisabled,
					error,
					countries,
					states: [],
					helperText,
					showAddressType,
					formLabel,
					editPerson,
					setSubmitting,
					addressBook,
				}}
			/>
			<AddressFormActions
				{...{ onCancel, submitLabel, cancelLabel, submitting, editPerson, hasPrimaryAdd }}
			/>
		</Stack>
	);
};
